import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CustomTable } from "./CustomTable";
import { TablePagination } from "../ui/table-pagination";
import { cn } from "@/lib/utils";

export interface ColumnDef {
  id: string;
  header: string;
  accessorKey?: string;
  cell?: (row: any) => React.ReactNode;
  sortable?: boolean;
  minWidth?: number;
  width?: number;
  hide?: boolean;
  editable?: boolean;
  inputType?: "text" | "number" | "date" | "select" | "multiselect" | "url" | "textarea";
  selectOptions?: string[] | { label: string; value: string }[] | ((row: any) => string[]);
  freezeLeft?: boolean;
  enableColumnFilter?: boolean;
  filterFn?: any;
  filterSelectOptions?: string[];
}

interface CustomDataTableProps {
  data: any[];
  columns: ColumnDef[];
  loading?: boolean;
  enableRowSelection?: boolean;
  selectedRows?: string[];
  onRowSelectionChange?: (rowIds: string[]) => void;
  onRowEdit?: (newRow: any, oldRow: any) => Promise<any>;
  enableColumnResizing?: boolean;
  onColumnVisibilityChange?: (newVisibility: Record<string, boolean>) => void;
  defaultColumnVisibility?: Record<string, boolean>;
  onColumnWidthChange?: (columnKey: string, newWidth: number) => void;
  currentColumnWidths?: Record<string, number>;
  enableSorting?: boolean;
  checkboxSelection?: boolean;
  // Pagination props
  totalItems?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  showPagination?: boolean;
  // Database sorting props
  dbSortColumn?: string | null;
  dbSortDirection?: 'asc' | 'desc' | null;
  onDbSort?: (column: string | null, direction: 'asc' | 'desc' | null) => void;
  disableClientSorting?: boolean;
}

export function CustomDataTable({
  data,
  columns,
  loading = false,
  enableRowSelection = false,
  selectedRows = [],
  onRowSelectionChange,
  onRowEdit,
  enableColumnResizing = false,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  onColumnWidthChange,
  currentColumnWidths,
  enableSorting = false,
  checkboxSelection = false,
  // Pagination props - using 1-based indexing
  totalItems,
  pageSize = 50,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  showPagination = true,
  // Database sorting props
  dbSortColumn,
  dbSortDirection,
  onDbSort,
  disableClientSorting = false,
}: CustomDataTableProps) {
  // Calculate total pages based on total items
  const totalPages = useMemo(() => {
    if (!totalItems || totalItems === 0) return 1;
    return Math.ceil(totalItems / pageSize);
  }, [totalItems, pageSize]);

  // Show pagination when there are multiple pages OR when we have pagination controls enabled
  const shouldShowPagination = showPagination && totalPages > 1;

  // Enable checkbox selection if either checkboxSelection is true OR enableRowSelection is true
  const showCheckboxes = checkboxSelection || enableRowSelection;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow overflow-hidden">
        <CustomTable
          data={data}
          columns={columns}
          loading={loading}
          enableRowSelection={enableRowSelection}
          selectedRows={selectedRows}
          onRowSelectionChange={onRowSelectionChange}
          onRowEdit={onRowEdit}
          enableColumnResizing={enableColumnResizing}
          onColumnVisibilityChange={onColumnVisibilityChange}
          defaultColumnVisibility={defaultColumnVisibility}
          onColumnWidthChange={onColumnWidthChange}
          currentColumnWidths={currentColumnWidths}
          enableSorting={enableSorting}
          checkboxSelection={showCheckboxes}
          showPagination={false}
          // Pass through database sorting props
          dbSortColumn={dbSortColumn}
          dbSortDirection={dbSortDirection}
          onDbSort={onDbSort}
          disableClientSorting={disableClientSorting}
        />
      </div>
      
      {shouldShowPagination && totalItems && (
        <div className="mt-4 border-t pt-4">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange || (() => {})}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            showPageSizeSelect={!!onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
}
